import useFetchAndTrace, { FetchAndTraceErrorHandler } from "./fetchWithTrace";
import { useState } from "react";
import { FormikHelpers } from "formik";
import { track } from "@amplitude/analytics-browser";
import settings from "../../constants/settings";
import { FormikState } from "formik/dist/types";
import useTrackGoogleEvent, {
  NEWSLETTER_SIGNUP_EVENT,
} from "../observability/googleTagManager";

interface SignUpOptions {
  source?: string;
  onSuccess?: () => void;
}

export interface SignUpFormValues {
  firstName?: string;
  lastName?: string;
  email: string;
  source: string;
}

export const useEmailMarketingSignUp = (options?: SignUpOptions) => {
  const fetchAndTrace = useFetchAndTrace();
  const [validationError, setValidationError] = useState<string | null>(null);
  const [errored, setErrored] = useState(false);
  const [loading, setLoading] = useState(false);
  const trackGoogleEvent = useTrackGoogleEvent();

  const handler = (
    resetForm: (nextState?: Partial<FormikState<SignUpFormValues>>) => void,
  ) => {
    return async (response: Response) => {
      if (response.ok) {
        options?.onSuccess?.();
        resetForm();
        setLoading(false);
        trackGoogleEvent({
          action: NEWSLETTER_SIGNUP_EVENT,
        });
      } else {
        if (response.status == 400) {
          const { detail: errorMessage } = (await response.json()) as {
            detail: string;
          };
          setValidationError(errorMessage);
          setLoading(false);
        } else {
          throw Error(
            `Add marketing contact responded with error code: ${response.status}`,
          );
        }
      }
    };
  };

  const errorHandler: FetchAndTraceErrorHandler = () => {
    setLoading(false);
    setErrored(true);
  };

  const signUp = (
    values: SignUpFormValues,
    { resetForm }: FormikHelpers<SignUpFormValues>,
  ) => {
    setLoading(true);
    setErrored(false);
    setValidationError(null);
    track("Sign up for beta", {
      email: values.email,
    });
    void fetchAndTrace(
      `${settings.api_url}/add-marketing-contact`,
      handler(resetForm),
      errorHandler,
      {
        body: JSON.stringify({
          email: values.email,
          first_name: values.firstName,
          last_name: values.lastName,
          source: values.source,
        }),
        method: "PUT",
      },
    );
  };

  return {
    signUp,
    loading,
    errored,
    validationError,
  };
};
